import React, { lazy, Suspense, useEffect } from "react";
import { Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { RequireEntitlement } from "@/components/setup/require-entitlement";
import { useAuthStore } from "@/store/auth-store";
import { assertAppDocumentTitle } from "@/lib/document-title";
import { safeNextPath } from "@/lib/safe-next-path";
import { rememberSupportPage } from "@/pages/support/page-memory";
import { isAppLike } from "@/lib/platform";
import { isQaEnv } from "@/lib/qa-env";
import { QA_TICKET_ROLES } from "@/lib/role-access";
import { RequirePreview } from "@/components/auth/require-preview";
import { DesktopOnly } from "@/components/layout/desktop-only";
import { APP_ORIGIN, isMarketingDomain, isMarketingPath } from "@/lib/marketing-domain";
import { FACE_SCAN_ROLES } from "@/pages/attendance/face-scan/face-scan-access";
import { SignedOutFallback } from "@/components/auth/signed-out-fallback";
// The shell's stylesheets stay in the launch stylesheet, in the order they
// had when the shell was imported here eagerly. The JS moved behind lazy()
// below; the CSS deliberately did not. Two things depend on it being present
// from the first paint and in this exact cascade position: rules elsewhere
// that win or lose against these by source order (module-lock.css before,
// update-sheet.css after), and surfaces outside the shell that pick some of
// these rules up — the Control Center's `.cc-nav` takes its font from
// command-center-shell.css. Loading them with the shell chunk would change
// both. That CSS is ~9 KB gzipped; the JS that left the launch path is ~160 KB.
import "@/components/layout/command-center-shell.css";
import "@/components/brand/brand-logo.css";
import "@/components/layout/sidebar-brand.css";
import "@/components/setup/setup.css";
import "@/components/layout/native-brand-bar.css";
import "@/components/layout/more-sheet.css";
import "@/components/communication/announcement-force-block-overlay.css";
import "@/components/walkthrough/walkthrough.css";
import "@/components/daily-brief/daily-brief.css";

// The signed-in chrome (sidebar, top bar, bottom nav, alert dock and the
// first-run gates it mounts) is about half of what the launch chunk used to
// carry. The login page, the marketing site and every public verify / give
// page never render it, so it loads behind its own boundary. For a session
// that will need it, the fetch starts the moment this module evaluates — in
// parallel with React's first render and the native session restore — so a
// signed-in launch does not wait an extra round trip for it.
const loadAppShell = () => import("@/components/layout/app-shell");
const AppShell = lazy(() => loadAppShell().then((m) => ({ default: m.AppShell })));
if (typeof window !== "undefined" && useAuthStore.getState().isAuthenticated) {
  // Warm-up only: a failure here is not an error the user can act on. The
  // lazy() call above repeats the import and its outcome is what renders
  // (a stale-deploy 404 reaches the ErrorBoundary exactly as any page does).
  loadAppShell().catch(() => {});
}

// Public marketing site (apps/web/src/pages/site). Every page is lazy so the
// signed-in bundle never pays for it.
const SiteShell = lazy(() => import("@/pages/site/shell"));
const SiteHome = lazy(() => import("@/pages/site/pages/home"));
const SitePlatform = lazy(() => import("@/pages/site/pages/platform").then((m) => ({ default: m.PlatformHub })));
const SiteCapabilities = lazy(() => import("@/pages/site/pages/platform").then((m) => ({ default: m.Capabilities })));
const SiteIntegrations = lazy(() => import("@/pages/site/pages/platform").then((m) => ({ default: m.Integrations })));
const SiteProductDetail = lazy(() => import("@/pages/site/pages/product-detail"));
const SiteAi = lazy(() => import("@/pages/site/pages/product-detail").then((m) => ({ default: m.AiOverview })));
const SiteSolutions = lazy(() => import("@/pages/site/pages/solutions").then((m) => ({ default: m.SolutionsHub })));
const SiteSolutionDetail = lazy(() => import("@/pages/site/pages/solutions").then((m) => ({ default: m.SolutionDetail })));
const SiteImplementation = lazy(() => import("@/pages/site/pages/solutions").then((m) => ({ default: m.Implementation })));
const SiteRoadmap = lazy(() => import("@/pages/site/pages/solutions").then((m) => ({ default: m.Roadmap })));
const SiteCustomers = lazy(() => import("@/pages/site/pages/customers").then((m) => ({ default: m.CustomersHub })));
const SiteStory = lazy(() => import("@/pages/site/pages/customers").then((m) => ({ default: m.StoryDetail })));
const SitePricing = lazy(() => import("@/pages/site/pages/pricing").then((m) => ({ default: m.Pricing })));
const SiteWhy = lazy(() => import("@/pages/site/pages/pricing").then((m) => ({ default: m.WhyCampusHub })));
const SiteSecurity = lazy(() => import("@/pages/site/pages/pricing").then((m) => ({ default: m.Security })));
const SiteAbout = lazy(() => import("@/pages/site/pages/company").then((m) => ({ default: m.About })));
const SiteComingSoon = lazy(() => import("@/pages/site/pages/coming-soon"));
const SitePhilosophy = lazy(() => import("@/pages/site/pages/philosophy"));
const SiteByteGo = lazy(() => import("@/pages/site/pages/company").then((m) => ({ default: m.ByteGo })));
const SiteCareers = lazy(() => import("@/pages/site/pages/company").then((m) => ({ default: m.Careers })));
const SitePartners = lazy(() => import("@/pages/site/pages/company").then((m) => ({ default: m.Partners })));
const SiteResources = lazy(() => import("@/pages/site/pages/resources").then((m) => ({ default: m.ResourcesHub })));
const SiteResource = lazy(() => import("@/pages/site/pages/resources").then((m) => ({ default: m.ResourceDetail })));
const SiteFaq = lazy(() => import("@/pages/site/pages/faq"));
const SiteContact = lazy(() => import("@/pages/site/pages/contact").then((m) => ({ default: m.Contact })));
const SiteDemo = lazy(() => import("@/pages/site/pages/contact").then((m) => ({ default: m.Demo })));
const SiteSupport = lazy(() => import("@/pages/site/pages/contact").then((m) => ({ default: m.Support })));
const SiteRegister = lazy(() => import("@/pages/site/pages/register").then((m) => ({ default: m.Register })));
const RegisterPrintPage = lazy(() => import("@/pages/register-print"));
const LoginPage = lazy(() => import("@/pages/auth/login"));
const NativeLoginPage = lazy(() => import("@/pages/auth/native-login"));
const SignupPage = lazy(() => import("@/pages/auth/signup"));
const TourPage = lazy(() => import("@/pages/tour"));
const ForgotPasswordPage = lazy(() => import("@/pages/auth/forgot-password"));
const ResetPasswordPage = lazy(() => import("@/pages/auth/reset-password"));
const HandoffPage = lazy(() => import("@/pages/auth/handoff"));
const OnboardingPage = lazy(() => import("@/pages/onboarding"));
const DashboardPage = lazy(() => import("@/pages/dashboard"));
const StudentsPage = lazy(() => import("@/pages/students"));
const TeachersPage = lazy(() => import("@/pages/teachers"));
const AttendancePage = lazy(() => import("@/pages/attendance"));
const KioskAuthGate = lazy(() => import("@/pages/attendance/staff-kiosk").then((m) => ({ default: m.KioskAuthGate })));
const FaceScanPage = lazy(() => import("@/pages/attendance/face-scan/face-scan-page").then((m) => ({ default: m.FaceScanPage })));
const TimetablePage         = lazy(() => import("@/pages/timetable"));
const LessonsPage           = lazy(() => import("@/pages/lessons"));
const ExamsPage = lazy(() => import("@/pages/exams"));
// WP-7c — the assessment-model pages under /exams. Each is its own route (declared before `exams/*`), gated here
// with the same role list the API's `authorize()` carries.
const ReviewQueuePage = lazy(() => import("@/pages/exams/review-queue").then((m) => ({ default: m.ReviewQueuePage })));
const ReEvaluationQueuePage = lazy(() => import("@/pages/exams/re-evaluation-queue").then((m) => ({ default: m.ReEvaluationQueuePage })));
const CoScholasticPage = lazy(() => import("@/pages/exams/co-scholastic").then((m) => ({ default: m.CoScholasticPage })));
const PromotionPage = lazy(() => import("@/pages/exams/promotion").then((m) => ({ default: m.PromotionPage })));
const ReportCardVerifyPage = lazy(() => import("@/pages/verify/report-card").then((m) => ({ default: m.ReportCardVerifyPage })));
const FinancePage = lazy(() => import("@/pages/finance"));
const HRPage = lazy(() => import("@/pages/hr"));
const LibraryPage = lazy(() => import("@/pages/library"));
const TransportPage = lazy(() => import("@/pages/transport"));
const DrivePage = lazy(() => import("@/pages/drive"));
const CommunicationPage = lazy(() => import("@/pages/communication"));
const FeedbackPage = lazy(() => import("@/pages/feedback"));
// CAMP-2026-0772 — campaign creation is its own page, not a card inside
// the Feedback & Insights dashboard.
const CreateCampaignPage = lazy(() => import("@/pages/campaigns/create"));
const CalendarPage = lazy(() => import("@/pages/calendar"));
const AnalyticsPage = lazy(() => import("@/pages/analytics"));
const SettingsPage = lazy(() => import("@/pages/settings"));
const HomeworkPage = lazy(() => import("@/pages/homework"));
const MyDayPage = lazy(() => import("@/pages/my-day").then((m) => ({ default: m.MyDayPage })));
const ClassDailyUpdatePage = lazy(() =>
  import("@/pages/my-day/class-daily-update").then((m) => ({ default: m.ClassDailyUpdatePage })),
);
const DigitalLearnerPage = lazy(() => import("@/pages/digital-learner"));
const NewsletterPage = lazy(() => import("@/pages/newsletter"));
const DigitalLearningAiPage = lazy(() => import("@/pages/digital-learning-ai"));
const DigitalLearningAiTutorPage = lazy(() => import("@/pages/digital-learning-ai/tutor"));
const CurriculumPage = lazy(() => import("@/pages/curriculum"));
const HostelPage = lazy(() => import("@/pages/hostel"));
const HealthPage = lazy(() => import("@/pages/health"));
const AdmissionsPage = lazy(() => import("@/pages/admissions"));
const VirtualClassroomPage = lazy(() => import("@/pages/classroom"));
const MeetingsPage = lazy(() => import("@/pages/meetings"));
const GradebookPage = lazy(() => import("@/pages/gradebook"));
const HallTicketsPage = lazy(() => import("@/pages/hall-tickets").then((m) => ({ default: m.HallTicketsPage })));
const OnlineExamPage = lazy(() => import("@/pages/exams/take"));
const CareerPage = lazy(() => import("@/pages/career"));
const CanteenPage = lazy(() => import("@/pages/canteen"));
const CounsellingPage = lazy(() => import("@/pages/counselling"));
// CAMP-2026-0970 — the family (parent / student) view of counselling appointments.
const FamilyCounsellingPage = lazy(() => import("@/pages/counselling/family"));
const EarlyWarningPage = lazy(() => import("@/pages/early-warning"));
const FaircopyPage = lazy(() => import("@/pages/faircopy"));
const CompliancePage = lazy(() => import("@/pages/compliance"));
const IoTPage = lazy(() => import("@/pages/iot"));
const CertificatesPage = lazy(() => import("@/pages/certificates/certificates-page").then((m) => ({ default: m.CertificatesPage })));
const CertificateViewerPage = lazy(() => import("@/pages/certificates/certificate-viewer-page").then((m) => ({ default: m.CertificateViewerPage })));
const VerifyCertificatePage = lazy(() => import("@/pages/certificates/verify-certificate-page").then((m) => ({ default: m.VerifyCertificatePage })));
const RecognitionPage = lazy(() => import("@/pages/recognition/recognition-page").then((m) => ({ default: m.RecognitionPage })));
const IdCardsPage = lazy(() => import("@/pages/id-cards/id-cards-page").then((m) => ({ default: m.IdCardsPage })));
const VerifyIdCardPage = lazy(() => import("@/pages/id-cards/verify-id-card-page").then((m) => ({ default: m.VerifyIdCardPage })));
const GatePassesPage = lazy(() => import("@/pages/passes/gate-passes-page").then((m) => ({ default: m.GatePassesPage })));
const VisitorPassesPage = lazy(() => import("@/pages/passes/visitor-passes-page").then((m) => ({ default: m.VisitorPassesPage })));
const PassVerifyPage = lazy(() => import("@/pages/passes/pass-verify-page").then((m) => ({ default: m.PassVerifyPage })));
const EventTicketsPage = lazy(() => import("@/pages/event-tickets/event-tickets-page").then((m) => ({ default: m.EventTicketsPage })));
const EventTicketDetailPage = lazy(() => import("@/pages/event-tickets/event-detail-page").then((m) => ({ default: m.EventTicketDetailPage })));
// CAMP-2026-0973 — a parent's / student's own event tickets.
const MyTicketsPage = lazy(() => import("@/pages/event-tickets/my-tickets-page").then((m) => ({ default: m.MyTicketsPage })));
const VerifyTicketPage = lazy(() => import("@/pages/event-tickets/verify-ticket-page").then((m) => ({ default: m.VerifyTicketPage })));
const PostersPage = lazy(() => import("@/pages/posters/posters-page").then((m) => ({ default: m.PostersPage })));
const WebsitePage = lazy(() => import("@/pages/website").then((m) => ({ default: m.WebsitePage })));
const StudioHomePage = lazy(() => import("@/pages/studio/studio-home").then((m) => ({ default: m.StudioHomePage })));
const StudioVerifyPage = lazy(() => import("@/pages/studio/verify"));
const HallTicketVerifyPage = lazy(() => import("@/pages/hall-tickets/verify").then((m) => ({ default: m.HallTicketVerifyPage })));
const StudioNewsletterPage = lazy(() => import("@/pages/studio/newsletter"));
const HubbaPage = lazy(() => import("@/pages/hubba"));
const FuturePage = lazy(() => import("@/pages/future"));
const AiTutorPage = lazy(() => import("@/pages/ai-tutor"));
const NotificationsPage = lazy(() => import("@/pages/notifications"));
const AutomationPage = lazy(() => import("@/pages/automation"));
const AlumniPage = lazy(() => import("@/pages/alumni"));
const ScienceLabPage = lazy(() => import("@/pages/science-lab"));
const NeuralPage = lazy(() => import("@/pages/neural"));
const MetaversePage = lazy(() => import("@/pages/metaverse"));
const ScholarshipsPage = lazy(() => import("@/pages/scholarships/index"));
const CPDPage = lazy(() => import("@/pages/cpd/index"));
const CompetencyPage = lazy(() => import("@/pages/competency/index"));
const HPCardPage = lazy(() => import("@/pages/hpcard/index"));
const ForumsPage = lazy(() => import("@/pages/forums/index"));
const PTMPage = lazy(() => import("@/pages/ptm/index"));
const SurveysPage = lazy(() => import("@/pages/surveys/index"));
const SpecialEducationPage = lazy(() => import("@/pages/special-education/index"));
const SELPage = lazy(() => import("@/pages/sel/index"));
const AdaptiveLearningPage = lazy(() => import("@/pages/adaptive-learning/index"));
const SCORMPage = lazy(() => import("@/pages/scorm/index"));
const LearningPage = lazy(() => import("@/pages/learning/index"));
// Brain Arcade. Every one of these is lazy. There is no engine chunk any more —
// the play route used to pull Phaser (~1.2MB, its own vendor-phaser split) and
// is now plain DOM, so the whole module is a few KB of components.
const ArcadeHub = lazy(() => import("@/pages/playground/hub"));
const ArcadeGameDetail = lazy(() => import("@/pages/playground/game-detail"));
const ArcadeGamePlay = lazy(() => import("@/pages/playground/game-play"));
const ArcadeTeacherGames = lazy(() => import("@/pages/playground/teacher-games"));
const ArcadeAssignmentDetail = lazy(() => import("@/pages/playground/assignment-detail"));
const ArcadeAnalytics = lazy(() => import("@/pages/playground/analytics"));
const ArcadeAdminLibrary = lazy(() => import("@/pages/playground/admin-library"));
const BiometricPage = lazy(() => import("@/pages/biometric/index"));
const UDISEPage = lazy(() => import("@/pages/udise/index"));
const PlagiarismPage = lazy(() => import("@/pages/plagiarism/index"));
const IntegrationsPage = lazy(() => import("@/pages/integrations/index"));
const BadgesPage = lazy(() => import("@/pages/badges/badges-page").then((m) => ({ default: m.BadgesPage })));
// Donations & Fundraising: the module shell owns its own sub-router (donations/*).
const DonationsModule = lazy(() => import("@/pages/donations/index").then((m) => ({ default: m.DonationsModule })));
// Public giving pages — no auth, no shell (school transparency page, campaign page, receipt check).
const PublicTransparencyPage = lazy(() => import("@/pages/give/index").then((m) => ({ default: m.PublicTransparencyPage })));
const PublicCampaignPage = lazy(() => import("@/pages/give/campaign").then((m) => ({ default: m.PublicCampaignPage })));
const ReceiptVerifyPage = lazy(() => import("@/pages/give/verify-receipt").then((m) => ({ default: m.ReceiptVerifyPage })));
const CommunicationsPage = lazy(() => import("@/pages/communications/index"));
const WhatsAppPage = lazy(() => import("@/pages/whatsapp/index"));
const BudgetPage = lazy(() => import("@/pages/budget/index"));
const DPDPPage = lazy(() => import("@/pages/dpdp/index"));
const AuditLogPage = lazy(() => import("@/pages/audit").then((m) => ({ default: m.AuditLogPage })));
const SubstitutesPage = lazy(() =>
  import("@/pages/substitutes").then((m) => ({ default: m.SubstitutesPage })),
);
const NotFoundPage = lazy(() => import("@/pages/not-found"));
const PrivacyPage = lazy(() => import("@/pages/legal/privacy"));
const TermsPage = lazy(() => import("@/pages/legal/terms"));
const DeleteAccountPage = lazy(() => import("@/pages/legal/delete-account"));
const SchoolsPage = lazy(() => import("@/pages/schools/index"));
// NOT /health (Health & Safety — clinic, medical records, meals). This is the
// picked school's performance rating; see pages/school-health/index.tsx.
const SchoolHealthPage = lazy(() =>
  import("@/pages/school-health/index").then((m) => ({ default: m.SchoolHealthPage })),
);
const QaPage = lazy(() => import("@/pages/qa/index"));
const InvitationAcceptPage = lazy(() => import("@/pages/invitations/accept"));
const DashboardBuilderPage = lazy(() =>
  import("@/pages/settings/dashboard-builder").then((m) => ({ default: m.DashboardBuilderPage })),
);
const ClassesSetupPage = lazy(() => import("@/pages/settings/classes-setup"));
const ConcessionApprovalsPage = lazy(() => import("@/pages/concession-approvals"));
// CAMP-2026-0884 — every approval type on one page (the dashboard queue, full width).
const ApprovalsPage = lazy(() => import("@/pages/approvals").then((m) => ({ default: m.ApprovalsPage })));
const TicketsPage = lazy(() => import("@/pages/tickets"));
// The customer-facing Support desk (/support). The QA board above is the platform team's.
const SupportPage = lazy(() => import("@/pages/support").then((m) => ({ default: m.SupportPage })));
const E2eRunsPage = lazy(() => import("@/pages/e2e-runs").then((m) => ({ default: m.E2eRunsPage })));
const TestingPlanPage = lazy(() => import("@/pages/testing-plan"));
const GuestJoinPage = lazy(() => import("@/pages/meetings/guest-join").then((m) => ({ default: m.GuestJoinPage })));
const SupportMeetingPage = lazy(() => import("@/pages/support/support-meeting-page").then((m) => ({ default: m.SupportMeetingPage })));
const ClientOnboardingPage = lazy(() => import("@/pages/client-onboarding"));

function PageLoader() {
  return (
    <div className="flex-1 flex items-center justify-center min-h-[400px]">
      <div className="flex flex-col items-center gap-4">
        <div
          className="w-8 h-8 rounded-full border-2 animate-spin"
          style={{
            borderColor: "var(--color-brand-border)",
            borderTopColor: "var(--color-brand)",
          }}
        />
        <p className="text-sm" style={{ color: "var(--color-text-3)" }}>
          Loading…
        </p>
      </div>
    </div>
  );
}

/** Shown for the moment the shell chunk is in flight: the same loader the
 *  shell's own content area shows while a page loads, on the app background. */
function ShellLoader() {
  return (
    <div className="flex h-[100dvh] w-full bg-app-bg">
      <PageLoader />
    </div>
  );
}

function Wrap({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

function AdmissionsIdRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/admissions?focus=${id ?? ""}`} replace />;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { pathname, search } = useLocation();
  if (!isAuthenticated) {
    const next = encodeURIComponent(pathname + search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <>{children}</>;
}

function RequireRole({ children, roles }: { children: React.ReactNode; roles: string[] }) {
  const role = useAuthStore((s) => s.user?.role ?? "");
  if (!roles.includes(role)) {
    // DRIVERs land on /drive (their entire app), not /dashboard.
    if (role === "DRIVER") return <Navigate to="/drive" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}

// Layout-root index redirect: DRIVERs go straight to /drive on login,
// everyone else lands on /dashboard.
function RoleAwareHomeRedirect() {
  const role = useAuthStore((s) => s.user?.role ?? "");
  if (role === "DRIVER") return <Navigate to="/drive" replace />;
  if (role === "KIOSK")  return <Navigate to="/attendance/kiosk" replace />;
  return <Navigate to="/dashboard" replace />;
}

// /dashboard for DRIVER/KIOSK would be empty — redirect them away.
function RoleAwareDashboardOrDrive({ children }: { children: React.ReactNode }) {
  const role = useAuthStore((s) => s.user?.role ?? "");
  if (role === "DRIVER") return <Navigate to="/drive" replace />;
  if (role === "KIOSK")  return <Navigate to="/attendance/kiosk" replace />;
  return <>{children}</>;
}

// Reusable role tuples — keep these in lockstep with the API
// `authorize()` calls. Centralising them so we never miss a role on a
// new high-value route (CAMP-2026-0109 happened because /finance had
// NO router-level gate at all and only RoleAccessGate stood between
// a TEACHER and the fee management hero).
const ADMIN_TIER = ["CHAIRMAN", "ADMIN", "DIRECTOR", "PRINCIPAL"];
const ADMIN_TIER_PLUS_STAFF = [...ADMIN_TIER, "STAFF"];
// `/testing-plan/*` (routes/testing-plan): the platform team only.
const TESTING_PLAN_ROLES = ["CONTROL_CENTER", "CHAIRMAN"];
// Live support (2026-10-09): a ByteGo support person joins a school meeting from
// /support/meeting/:id — platform tier only; the API admits them only while the
// host has switched support access on.
const SUPPORT_MEETING_ROLES = ["CONTROL_CENTER"];
// The internal QA board (/tickets): the platform team's tool, not a customer's.
// Customers raise requests at /support instead (routes/support). In a QA
// environment every desk role uses it (QA_TICKET_ROLES, lib/role-access.ts).
const TICKETS_BOARD_ROLES = ["CONTROL_CENTER", "CHAIRMAN"];
// Who may open /support — the API's SUPPORT_ROLES (routes/support/support-core.ts).
// DRIVER and KIOSK are not people at a desk; CONTROL_CENTER works the board.
const SUPPORT_ROLES = [...ADMIN_TIER, "TEACHER", "STAFF", "PARENT", "STUDENT"];
// Who may open the AI Teacher — the API's `AI_TEACHER_ROLES`
// (apps/api/src/lib/lavekai-assertion.ts) says the same.
const AI_TEACHER_ROLES = ["PARENT", "STUDENT", "CHAIRMAN", "ADMIN", "DIRECTOR", "PRINCIPAL", "TEACHER", "STAFF"];

export function AppRouter() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  // App-like shells (Capacitor iOS/Android + installed PWAs) get a
  // fundamentally different unauth surface: the marketing pages are never
  // mounted (App Store / Play Store reviewers expect an app, not a content
  // site), self-signup is hidden because schools onboard via the web flow,
  // and the only login surface is the premium NativeLoginPage.
  //
  // `isAppLike()` is true for both the native shell and a standalone PWA.
  // The variable is still named `native` because it gates the same set of
  // branches and renaming touches a lot of conditional JSX.
  const native = isAppLike();
  // Authenticated but onboarding not finished — never mount AppShell (it runs
  // useDashboardSync / Sidebar queries that fail with "No school context").
  const needsOnboarding = isAuthenticated && !user?.onboardingComplete;
  // Reactive check for pending registration token (new user, not yet in DB)
  const [hasPendingToken, setHasPendingToken] = React.useState(
    () => Boolean(sessionStorage.getItem("pendingToken")),
  );
  // Sync when storage events fire (e.g. navigate from signup sets the token)
  // CAMP-2026-0680 — every navigation restores the app's tab title unless the
  // page being shown owns it (the marketing pages claim it in `useSeo`). This
  // is what stops the prerendered home page's <head>, which the host serves as
  // the SPA fallback for every route, from leaving a marketing title on
  // /dashboard or /login.
  const { pathname: routerPathname, search: routerSearch } = useLocation();
  // A platform (CONTROL_CENTER) account signing in for a support call has no school to set up:
  // its `?next=/support/meeting/…` wins over /onboarding (the route is registered outside the
  // onboarding-gated blocks). Everything else about such an account is unchanged.
  const loginNext = safeNextPath(routerSearch);
  const supportCallNext = user?.role === "CONTROL_CENTER" && loginNext.startsWith("/support/meeting/");
  useEffect(() => {
    assertAppDocumentTitle();
    // Support asks nothing about "which page": it remembers the last one.
    rememberSupportPage(routerPathname);
  }, [routerPathname]);

  // Someone on the sign-in screen is about to need the shell. Fetch it once
  // the sign-in page has had two seconds to itself, so the hop to /dashboard
  // after a successful sign-in doesn't wait on it. One chunk, same bytes the
  // launch chunk used to carry; never fetched on any other public page.
  useEffect(() => {
    if (isAuthenticated || routerPathname !== "/login") return;
    const id = window.setTimeout(() => {
      loadAppShell().catch(() => {});
    }, 2000);
    return () => window.clearTimeout(id);
  }, [isAuthenticated, routerPathname]);

  // campushubai.com only hosts the marketing site. Client-side navigation to
  // an app page never reaches the edge redirect, so hand off here — the app
  // must run on campushub.bytego.ai (cookie domain + CORS). See
  // lib/marketing-domain.ts.
  useEffect(() => {
    if (isMarketingDomain() && !isMarketingPath(routerPathname)) {
      window.location.replace(`${APP_ORIGIN}${routerPathname}${routerSearch}`);
    }
  }, [routerPathname, routerSearch]);

  React.useEffect(() => {
    const check = () => setHasPendingToken(Boolean(sessionStorage.getItem("pendingToken")));
    window.addEventListener("storage", check);
    // Also poll on focus in case sessionStorage changed in this tab
    window.addEventListener("focus", check);
    return () => {
      window.removeEventListener("storage", check);
      window.removeEventListener("focus", check);
    };
  }, []);

  return (
    <Routes>
      <Route
        path="/login"
        element={
          isAuthenticated ? (
            // Send incomplete-onboarding users back to /onboarding. Everyone
            // else goes to the same-origin `?next=` the login page honours —
            // this element re-renders the moment the session commits, so
            // ignoring `next` here used to overwrite the login page's own
            // navigation with /dashboard.
            <Navigate to={needsOnboarding && !supportCallNext ? "/onboarding" : loginNext} replace />
          ) : native ? (
            // Native shell — premium full-bleed login (no brand panel split,
            // no Google SSO).
            <Wrap>
              <NativeLoginPage />
            </Wrap>
          ) : (
            <Wrap>
              <LoginPage />
            </Wrap>
          )
        }
      />
      <Route
        path="/signup"
        element={
          isAuthenticated ? (
            <Navigate to="/dashboard" replace />
          ) : native ? (
            // Native: schools onboard on the web (Apple/Google reviewers
            // also dislike "create account" surfaces that send users out
            // to a website) → /login.
            <Navigate to="/login" replace />
          ) : (
            <Wrap>
              <SignupPage />
            </Wrap>
          )
        }
      />
      {/* Public: the product walkthrough. Linkable on its own so a school can
          be sent straight to it; the app's sign-in screen renders the same
          player inline rather than navigating here. Open to signed-in users
          too — "show me that again" is a reasonable thing to want. */}
      <Route
        path="/tour"
        element={
          <Wrap>
            <TourPage />
          </Wrap>
        }
      />
      <Route
        path="/onboarding"
        element={
          isAuthenticated || hasPendingToken ? (
            <Wrap>
              <DesktopOnly
                native
                title="Finish setup on desktop"
                description="School onboarding is a guided setup best completed on a larger screen. Open CampusHub on a desktop to finish getting your school ready."
              >
                <OnboardingPage />
              </DesktopOnly>
            </Wrap>
          ) : (
            <Navigate to="/signup" replace />
          )
        }
      />
      {/* Public: password reset */}
      <Route
        path="/forgot-password"
        element={
          isAuthenticated ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <Wrap>
              <ForgotPasswordPage />
            </Wrap>
          )
        }
      />
      <Route
        path="/reset-password/:token"
        element={
          <Wrap>
            <ResetPasswordPage />
          </Wrap>
        }
      />
      {/* Control Center handoff — public; the one-shot ?token= IS the auth. */}
      <Route
        path="/auth/handoff"
        element={
          <Wrap>
            <HandoffPage />
          </Wrap>
        }
      />
      {/* Public: invited users create their account here */}
      <Route
        path="/invitations/accept/:token"
        element={
          <Wrap>
            <InvitationAcceptPage />
          </Wrap>
        }
      />
      {/* Legacy /home deep links → root */}
      <Route path="/home" element={<Navigate to="/" replace />} />
      {/* Public: printable paper version of /register. Own chrome, no site
          shell, noindex — so it is not prerendered (the CloudFront SPA
          rewrite serves the app shell for it). */}
      <Route
        path="/register/print"
        element={
          <Wrap>
            <RegisterPrintPage />
          </Wrap>
        }
      />
      {/* Public: legal pages */}
      <Route
        path="/legal/privacy"
        element={
          <Wrap>
            <PrivacyPage />
          </Wrap>
        }
      />
      <Route
        path="/legal/terms"
        element={
          <Wrap>
            <TermsPage />
          </Wrap>
        }
      />
      <Route
        path="/legal/delete-account"
        element={
          <Wrap>
            <DeleteAccountPage />
          </Wrap>
        }
      />
      {/* Public certificate check — what the QR on every certificate issued
          by the Certificates section points at (/v/<verifyToken>). No auth,
          no shell, noindex. A separate prefix from /verify/:token (Studio
          passes and hall tickets), so neither can swallow the other. */}
      {/* Public ticket check — what an event ticket's QR opens (/v/t/<token>).
          Masked holder name, never marks the ticket used (only the school's
          gate scanner does). */}
      <Route
        path="/v/t/:token"
        element={
          <Wrap>
            <VerifyTicketPage />
          </Wrap>
        }
      />
      <Route
        path="/v/:token"
        element={
          <Wrap>
            <VerifyCertificatePage />
          </Wrap>
        }
      />
      {/* Public ID-card check — what the QR on the back of every ID card
          issued by the ID Cards section opens (/verify-id/<verifyToken>).
          No auth, no shell, noindex. Its own prefix, declared before
          /verify/:token, so a Studio token can never swallow it. */}
      <Route
        path="/verify-id/:token"
        element={
          <Wrap>
            <VerifyIdCardPage />
          </Wrap>
        }
      />
      {/* Public pass check — what the QR on a gate pass, visitor pass or visit
          invite opens in a phone camera (/p/<token>). No auth, no shell,
          noindex; masked name and times only. Passes printed before the
          2026-09-27 rebuild point at /verify/<token>, which the Studio verify
          page forwards here. */}
      <Route
        path="/p/:token"
        element={
          <Wrap>
            <PassVerifyPage />
          </Wrap>
        }
      />
      {/* Public Studio verify endpoint — what a Studio QR points at.
          No auth: a gate guard, event usher, or recipient must be able
          to confirm a pass/ticket is authentic from any phone. The
          frontend page calls /api/v1/studio/verify/:token directly. */}
      {/* Public hall-ticket verify — what the QR on a printed hall ticket
          opens. Declared BEFORE /verify/:token so "hall-ticket" is never
          swallowed as a Studio token. */}
      {/* Public donation-receipt check — the QR on every donation receipt.
          Declared BEFORE /verify/:token so "donation" is never read as a
          Studio token. No auth, no shell. */}
      <Route
        path="/verify/donation/:token"
        element={
          <Wrap>
            <ReceiptVerifyPage />
          </Wrap>
        }
      />
      {/* Public report-card check — the link / QR on an issued report card (WP-7c). Declared BEFORE
          /verify/:token so "report-card" is never read as a Studio token. No auth, no shell, noindex;
          it shows school, initials, year and version, never a mark. */}
      <Route
        path="/verify/report-card/:token"
        element={
          <Wrap>
            <ReportCardVerifyPage />
          </Wrap>
        }
      />
      {/* Public giving pages (opt-in per school in Donations → Settings). */}
      <Route
        path="/give/:schoolCode"
        element={
          <Wrap>
            <PublicTransparencyPage />
          </Wrap>
        }
      />
      <Route
        path="/give/:schoolCode/c/:slug"
        element={
          <Wrap>
            <PublicCampaignPage />
          </Wrap>
        }
      />
      <Route
        path="/verify/hall-ticket/:token"
        element={
          <Wrap>
            <HallTicketVerifyPage />
          </Wrap>
        }
      />
      <Route
        path="/verify/:token"
        element={
          <Wrap>
            <StudioVerifyPage />
          </Wrap>
        }
      />
      {/* Field-ops onboarding tracker — independent tool for ByteGo agents
          who set up school clients. Has its own mobile+PIN auth, completely
          separate from the main CampusHub session. */}
      <Route
        path="/client-onboarding"
        element={
          <Wrap>
            <ClientOnboardingPage />
          </Wrap>
        }
      />
      <Route
        path="/client-onboarding/t/:id"
        element={
          <Wrap>
            <ClientOnboardingPage />
          </Wrap>
        }
      />

      {/* Legacy /setup deep links now redirect to Members settings.
          Onboarding + first-login tour replace the old setup wizard. */}
      <Route path="/setup" element={<Navigate to="/settings/members" replace />} />

      {/* Public marketing pages — only when not authenticated AND not in
          the native shell. The Capacitor app deliberately ships zero
          marketing surfaces (App Store reviewers expect an "app", not a
          content site, and our marketing pages assume desktop layouts
          and a public web origin). The catch-all near the bottom of this
          Routes block sends every native unauth navigation back to /login. */}
      {!isAuthenticated && !native && (
        <Route element={<Wrap><SiteShell /></Wrap>}>
          <Route path="/" element={<Wrap><SiteHome /></Wrap>} />

          {/* Platform */}
          <Route path="/platform" element={<Wrap><SitePlatform /></Wrap>} />
          <Route path="/platform/capabilities" element={<Wrap><SiteCapabilities /></Wrap>} />
          <Route path="/integrations" element={<Wrap><SiteIntegrations /></Wrap>} />
          <Route path="/products/:slug" element={<Wrap><SiteProductDetail /></Wrap>} />
          <Route path="/ai" element={<Wrap><SiteAi /></Wrap>} />

          {/* Solutions */}
          <Route path="/solutions" element={<Wrap><SiteSolutions /></Wrap>} />
          <Route path="/solutions/:slug" element={<Wrap><SiteSolutionDetail /></Wrap>} />
          <Route path="/implementation" element={<Wrap><SiteImplementation /></Wrap>} />
          <Route path="/roadmap" element={<Wrap><SiteRoadmap /></Wrap>} />

          {/* Proof & commercials */}
          <Route path="/customers" element={<Wrap><SiteCustomers /></Wrap>} />
          <Route path="/customers/:slug" element={<Wrap><SiteStory /></Wrap>} />
          <Route path="/pricing" element={<Wrap><SitePricing /></Wrap>} />
          <Route path="/why-campushub" element={<Wrap><SiteWhy /></Wrap>} />
          <Route path="/security" element={<Wrap><SiteSecurity /></Wrap>} />

          {/* Company */}
          <Route path="/coming-soon" element={<Wrap><SiteComingSoon /></Wrap>} />
          <Route path="/philosophy" element={<Wrap><SitePhilosophy /></Wrap>} />
          <Route path="/about" element={<Wrap><SiteAbout /></Wrap>} />
          <Route path="/bytego" element={<Wrap><SiteByteGo /></Wrap>} />
          <Route path="/careers" element={<Wrap><SiteCareers /></Wrap>} />
          <Route path="/partners" element={<Wrap><SitePartners /></Wrap>} />

          {/* Resources & contact */}
          <Route path="/resources" element={<Wrap><SiteResources /></Wrap>} />
          <Route path="/resources/:slug" element={<Wrap><SiteResource /></Wrap>} />
          <Route path="/faq" element={<Wrap><SiteFaq /></Wrap>} />
          <Route path="/contact" element={<Wrap><SiteContact /></Wrap>} />
          <Route path="/demo" element={<Wrap><SiteDemo /></Wrap>} />
          <Route path="/register" element={<Wrap><SiteRegister /></Wrap>} />
          <Route path="/support" element={<Wrap><SiteSupport /></Wrap>} />

          {/* Legacy paths from the previous marketing site, kept so inbound
              links and any indexed URLs continue to resolve. */}
          {([
            ["/product", "/platform"],
            ["/features", "/platform/capabilities"],
            ["/experience", "/roadmap"],
            ["/hubba", "/products/hubba-ai"],
            ["/face-scan", "/products/attendance"],
            ["/smart-campus", "/products/smart-campus"],
            ["/robotics", "/platform"],
            ["/creative-studio", "/products/studio"],
            ["/ai-timetable", "/products/timetable"],
            ["/higher-education", "/solutions/higher-education"],
            ["/products/analytics", "/products/analytics"],
            ["/products/hubba-ai", "/products/hubba-ai"],
          ] as const)
            .filter(([from, to]) => from !== to)
            .map(([from, to]) => (
              <Route key={from} path={from} element={<Navigate to={to} replace />} />
            ))}

          {/* Anything else public falls back to the home page rather than a
              blank screen — the old site had no 404 either. A non-marketing
              path is an app deep link: it goes to /login?next= so the
              destination survives sign-in (components/auth/signed-out-fallback). */}
          <Route path="*" element={<SignedOutFallback />} />
        </Route>
      )}

      {/* Standalone QA tracker — no sidebar/topbar shell. The platform team's
          board (CONTROL_CENTER + CHAIRMAN only): a school's people raise
          requests at /support, which never links here. */}
      {isAuthenticated && (
        <Route
          path="/tickets"
          element={
            <ProtectedRoute>
              <RequireRole roles={isQaEnv() ? [...QA_TICKET_ROLES] : TICKETS_BOARD_ROLES}>
                <Wrap>
                  <TicketsPage />
                </Wrap>
              </RequireRole>
            </ProtectedRoute>
          }
        />
      )}

      {/* QA Command Center — Playwright runs. QA environments only: the route
          is not even registered in production, the "Run Tests" button is
          hidden there, and the API routes 404. */}
      {isAuthenticated && isQaEnv() && (
        <Route
          path="/e2e-runs"
          element={
            <ProtectedRoute>
              <Wrap>
                <E2eRunsPage />
              </Wrap>
            </ProtectedRoute>
          }
        />
      )}

      {/* Meeting guest link (2026-10-11) — an outsider joins from /join/<token> with no account.
          No app shell and no sign-in guard, registered for everyone (signed in or not, web or app);
          the API decides whether the link is live and the host admits each guest from the lobby. */}
      <Route
        path="/join/:token"
        element={
          <Wrap>
            <GuestJoinPage />
          </Wrap>
        }
      />

      {/* Attendance kiosk — standalone route, no app shell, no auth guard.
          Works when hit directly (tablet mounted at school gate). The
          KioskAuthGate handles its own login if no session exists. */}
      <Route
        path="/attendance/kiosk"
        element={
          <Wrap>
            <KioskAuthGate />
          </Wrap>
        }
      />

      {/* Jira-style testing plan board — companion to /tickets,
          same slim chrome, lives outside the app shell so QA can
          pop it open in a fresh tab. Every /testing-plan/* API route
          authorizes CONTROL_CENTER + CHAIRMAN only (and the module is not
          registered in real production), so nobody else gets the page. */}
      {/* Live support: the support person's call window. OUTSIDE the app shell (no school scope, no sidebar),
          registered whether or not someone is signed in so a signed-out visitor keeps `?next=` through /login. */}
      <Route
        path="/support/meeting/:id"
        element={
          <ProtectedRoute>
            <RequireRole roles={SUPPORT_MEETING_ROLES}>
              <Wrap>
                <SupportMeetingPage />
              </Wrap>
            </RequireRole>
          </ProtectedRoute>
        }
      />

      {isAuthenticated && (
        <Route
          path="/testingplan"
          element={
            <ProtectedRoute>
              <RequireRole roles={TESTING_PLAN_ROLES}>
                <Wrap>
                  <TestingPlanPage />
                </Wrap>
              </RequireRole>
            </ProtectedRoute>
          }
        />
      )}

      {/* AI Teacher (LavekAI) — full screen, OUTSIDE the app shell: no
          sidebar, top bar or school name, only the board. Same addresses as
          before; `/tutor` is the one older board links use. Roles match the
          API's /ai-teacher/assertion gate. */}
      {isAuthenticated && !needsOnboarding &&
        ["/digital-learner/ai-teacher", "/tutor"].map((path) => (
          <Route
            key={path}
            path={path}
            element={
              <ProtectedRoute>
                <RequireRole roles={AI_TEACHER_ROLES}>
                  {/* Outside the shell, so its plan gate is applied here:
                      the AI Teacher is part of the Digital Learner module. */}
                  <RequireEntitlement>
                    <Wrap>
                      {path === "/tutor" ? <DigitalLearningAiTutorPage /> : <DigitalLearningAiPage />}
                    </Wrap>
                  </RequireEntitlement>
                </RequireRole>
              </ProtectedRoute>
            }
          />
        ))}

      {/* Authenticated app — provides all dashboard sub-routes under `/` */}
      {/* Authenticated app shell — only mount after onboarding is complete.
           This prevents AppShell's useDashboardSync / Sidebar hooks from
           firing API calls while the user is still on /onboarding. */}
      {isAuthenticated && !needsOnboarding && (
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Suspense fallback={<ShellLoader />}>
                <AppShell />
              </Suspense>
            </ProtectedRoute>
          }
        >
          <Route index element={<RoleAwareHomeRedirect />} />
          <Route
            path="dashboard"
            element={
              <RoleAwareDashboardOrDrive>
                <Wrap>
                  <DashboardPage />
                </Wrap>
              </RoleAwareDashboardOrDrive>
            }
          />
          <Route
            path="students/*"
            element={
              <Wrap>
                <StudentsPage />
              </Wrap>
            }
          />
          <Route
            path="teachers/*"
            element={
              <Wrap>
                <TeachersPage />
              </Wrap>
            }
          />
          <Route
            path="attendance"
            element={
              <RequireRole
                roles={[
                  "TEACHER",
                  "PRINCIPAL",
                  "CHAIRMAN",
                  "ADMIN",
                  "DIRECTOR",
                  "PARENT",
                  "STUDENT",
                ]}
              >
                <Wrap>
                  <AttendancePage />
                </Wrap>
              </RequireRole>
            }
          />
          {/* Student face scan: photograph the class, mark attendance from it.
              RequireRole is mandatory: role-access.ts prefix-matches
              /attendance, which also admits PARENT and STUDENT. Deliberately
              not under /face-scan (that path is a marketing redirect). */}
          <Route
            path="attendance/face-scan"
            element={
              <RequireRole roles={FACE_SCAN_ROLES}>
                <Wrap>
                  <FaceScanPage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="substitutes"
            element={
              <RequireRole roles={["TEACHER", "STAFF", "PRINCIPAL", "CHAIRMAN", "ADMIN", "DIRECTOR"]}>
                <Wrap>
                  <SubstitutesPage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="timetable/*"
            element={
              <Wrap>
                <TimetablePage />
              </Wrap>
            }
          />
          <Route
            path="lessons"
            element={
              <RequireRole roles={["TEACHER", "PRINCIPAL", "CHAIRMAN", "ADMIN", "DIRECTOR"]}>
                <Wrap>
                  <LessonsPage />
                </Wrap>
              </RequireRole>
            }
          />
          {/* Legacy route — the standalone "Teachers Updates" screen was
              merged into the single /lessons admin view. Redirect so old
              links / bookmarks / dashboard CTAs keep working. */}
          <Route path="teachers-updates" element={<Navigate to="/lessons" replace />} />
          <Route
            path="exams/take"
            element={
              <Wrap>
                <OnlineExamPage />
              </Wrap>
            }
          />
          <Route
            path="exams/:examId/take"
            element={
              <Wrap>
                <OnlineExamPage />
              </Wrap>
            }
          />
          {/* WP-7c — the assessment-model pages. Review queue, re-evaluation requests and promotion are the
              admin tier's (`GET /exams/review-queue`, `GET /exams/re-evaluation` staff view, `POST /exams/promotion`
              are admin-tier routes); co-scholastic grades are also a TEACHER's, for the classes they teach. A
              parent or pupil holds the `/exams` prefix (RoleAccessGate lets them through), so the role gate is HERE. */}
          <Route
            path="exams/review-queue"
            element={
              <RequireRole roles={ADMIN_TIER}>
                <Wrap>
                  <ReviewQueuePage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="exams/re-evaluation"
            element={
              <RequireRole roles={ADMIN_TIER}>
                <Wrap>
                  <ReEvaluationQueuePage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="exams/promotion"
            element={
              <RequireRole roles={ADMIN_TIER}>
                <Wrap>
                  <PromotionPage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="exams/co-scholastic"
            element={
              <RequireRole roles={[...ADMIN_TIER, "TEACHER"]}>
                <Wrap>
                  <CoScholasticPage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="exams/*"
            element={
              <Wrap>
                <ExamsPage />
              </Wrap>
            }
          />
          {/* Fee Management. ADMIN_TIER_PLUS_STAFF mirrors the API
              authorize() list on /fees — STAFF can record collections
              but TEACHER must never see the hero, even empty
              (CAMP-2026-0109). PARENT is excluded here because the
              parent fee tab lives under a separate route shape; we
              re-allow it via RoleAccessGate's PARENT prefix list. */}
          <Route
            path="finance/*"
            element={
              <RequireRole roles={[...ADMIN_TIER_PLUS_STAFF, "PARENT"]}>
                <Wrap>
                  <FinancePage />
                </Wrap>
              </RequireRole>
            }
          />
          {/* `/fees` has a title in the app bar but no page of its own; an old
              bookmark or a typed URL lands on Finance instead of a 404. */}
          <Route path="fees" element={<Navigate to="/finance" replace />} />
          <Route
            path="hr/*"
            element={
              <RequireRole
                roles={["TEACHER", "STAFF", "PRINCIPAL", "CHAIRMAN", "ADMIN", "DIRECTOR"]}
              >
                <Wrap>
                  <HRPage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="library"
            element={
              <Wrap>
                <LibraryPage />
              </Wrap>
            }
          />
          <Route
            path="transport/*"
            element={
              <Wrap>
                <TransportPage />
              </Wrap>
            }
          />
          <Route
            path="drive"
            element={
              <RequireRole roles={["DRIVER", "ADMIN", "PRINCIPAL", "CHAIRMAN", "DIRECTOR"]}>
                <Wrap>
                  <DrivePage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="communication/*"
            element={
              <Wrap>
                <CommunicationPage />
              </Wrap>
            }
          />
          <Route
            path="feedback/*"
            element={
              <Wrap>
                <FeedbackPage />
              </Wrap>
            }
          />
          {/* Customer-facing Support: my requests, new request, one request
              and its conversation. Structural (never switched off per school);
              roles = the API's /support role list. */}
          <Route
            path="support/*"
            element={
              <RequireRole roles={SUPPORT_ROLES}>
                <Wrap>
                  <SupportPage />
                </Wrap>
              </RequireRole>
            }
          />
          {/* CAMP-2026-0772 — standalone Campaigns module. Creating a campaign
              is a leadership action, so it carries the same ADMIN_TIER gate the
              API's POST /feedback/campaign enforces (LEADERSHIP_ROLES). */}
          <Route
            path="campaigns/create"
            element={
              <RequireRole roles={ADMIN_TIER}>
                <Wrap>
                  <CreateCampaignPage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="calendar"
            element={
              <Wrap>
                <CalendarPage />
              </Wrap>
            }
          />
          <Route
            path="analytics/*"
            element={
              <RequireRole roles={ADMIN_TIER}>
                <Wrap>
                  <AnalyticsPage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="settings/*"
            element={
              <Wrap>
                <SettingsPage />
              </Wrap>
            }
          />
          <Route
            path="homework"
            element={
              <Wrap>
                <HomeworkPage />
              </Wrap>
            }
          />
          {/* My Day — the pre-primary daily experience. The parent view and
              the teacher's capture screen are separate routes because they
              are different jobs, not two tabs of one screen. */}
          <Route
            path="my-day"
            element={
              <RequireRole roles={["PARENT"]}>
                <Wrap>
                  <MyDayPage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="my-day/class"
            element={
              <RequireRole roles={["TEACHER"]}>
                <Wrap>
                  <ClassDailyUpdatePage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="digital-learner"
            element={
              <RequireRole roles={["PARENT", "STUDENT", "CHAIRMAN", "ADMIN", "DIRECTOR", "PRINCIPAL", "TEACHER", "STAFF"]}>
                <Wrap>
                  <DigitalLearnerPage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="newsletter"
            element={
              <RequireRole roles={["PARENT", "STUDENT", "CHAIRMAN", "ADMIN", "DIRECTOR", "PRINCIPAL", "TEACHER", "STAFF"]}>
                <Wrap>
                  <NewsletterPage />
                </Wrap>
              </RequireRole>
            }
          />
          {/* ── Brain Arcade ─────────────────────────────────────────
              One `/playground` prefix for every role, so there is a single
              RequireRole gate and a single role-access entry to keep in step
              rather than a parallel set that drifts.

              The five legacy game routes (/playground/memory-match etc.) are
              redirected rather than deleted: they were live, students have
              them bookmarked, and a 404 is a worse answer than the hub. */}
          <Route
            path="playground"
            element={
              <RequireRole roles={["STUDENT", "PARENT"]}>
                <Wrap>
                  <ArcadeHub />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="playground/g/:slug"
            element={
              <RequireRole roles={["STUDENT", "PARENT", "TEACHER", "ADMIN", "PRINCIPAL", "CHAIRMAN", "DIRECTOR"]}>
                <Wrap>
                  <ArcadeGameDetail />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="playground/g/:slug/play"
            element={
              <RequireRole roles={["STUDENT", "PARENT"]}>
                <Wrap>
                  <ArcadeGamePlay />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="playground/assign"
            element={
              <RequireRole roles={["TEACHER", "ADMIN", "PRINCIPAL", "CHAIRMAN", "DIRECTOR"]}>
                <Wrap>
                  <ArcadeTeacherGames />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="playground/assignments/:id"
            element={
              <RequireRole roles={["TEACHER", "ADMIN", "PRINCIPAL", "CHAIRMAN", "DIRECTOR"]}>
                <Wrap>
                  <ArcadeAssignmentDetail />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="playground/analytics"
            element={
              <RequireRole roles={["TEACHER", "ADMIN", "PRINCIPAL", "CHAIRMAN", "DIRECTOR"]}>
                <Wrap>
                  <ArcadeAnalytics />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="playground/library"
            element={
              <RequireRole roles={["ADMIN", "PRINCIPAL", "CHAIRMAN", "DIRECTOR"]}>
                <Wrap>
                  <ArcadeAdminLibrary />
                </Wrap>
              </RequireRole>
            }
          />
          <Route path="playground/memory-match" element={<Navigate to="/playground/g/memory-match" replace />} />
          <Route path="playground/quick-math" element={<Navigate to="/playground/g/number-challenge" replace />} />
          <Route path="playground/number-sequence" element={<Navigate to="/playground/g/number-challenge" replace />} />
          <Route path="playground/element-match" element={<Navigate to="/playground/g/memory-match" replace />} />
          <Route path="playground/solar-system" element={<Navigate to="/playground/g/solar-system-challenge" replace />} />
          <Route
            path="curriculum"
            element={
              <Wrap>
                <CurriculumPage />
              </Wrap>
            }
          />
          <Route
            path="hostel"
            element={
              <Wrap>
                <HostelPage />
              </Wrap>
            }
          />
          {/* Health & Safety — clinic/medical records. authorize() on the
              API restricts this to CHAIRMAN/ADMIN/DIRECTOR/PRINCIPAL/STAFF
              (see apps/api/src/routes/health/index.ts); mirror it here so a
              family deep-link 403s at the route guard instead of rendering
              an empty shell. lib/role-access.ts keeps STUDENT/PARENT off
              this prefix for the same reason. */}
          <Route
            path="health"
            element={
              <RequireRole roles={ADMIN_TIER_PLUS_STAFF}>
                <Wrap>
                  <HealthPage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="admissions"
            element={
              <Wrap>
                <AdmissionsPage />
              </Wrap>
            }
          />
          <Route
            path="admissions/:id"
            element={<AdmissionsIdRedirect />}
          />
          <Route
            path="meetings"
            element={
              <Wrap>
                <MeetingsPage />
              </Wrap>
            }
          />
          <Route
            path="classroom"
            element={
              <Wrap>
                <VirtualClassroomPage />
              </Wrap>
            }
          />
          <Route
            path="classroom/:sessionId"
            element={
              <Wrap>
                <VirtualClassroomPage />
              </Wrap>
            }
          />
          <Route
            path="gradebook"
            element={
              <Wrap>
                <GradebookPage />
              </Wrap>
            }
          />
          {/* CAMP-2026-0310 — hall-ticket generation is a school-issued
              document + carries every student's fee position, so admin tier
              only (matches the authorize() list on /hall-tickets/*). */}
          <Route
            path="hall-tickets"
            element={
              <RequireRole roles={ADMIN_TIER}>
                <Wrap>
                  <HallTicketsPage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="career"
            element={
              <Wrap>
                <CareerPage />
              </Wrap>
            }
          />
          <Route
            path="canteen"
            element={
              <Wrap>
                <CanteenPage />
              </Wrap>
            }
          />
          <Route
            path="counselling"
            element={
              <Wrap>
                <CounsellingPage />
              </Wrap>
            }
          />
          <Route
            path="counselling/my"
            element={
              <Wrap>
                <RequireRole roles={["PARENT", "STUDENT"]}>
                  <FamilyCounsellingPage />
                </RequireRole>
              </Wrap>
            }
          />
          <Route
            path="early-warning"
            element={
              <Wrap>
                <EarlyWarningPage />
              </Wrap>
            }
          />
          <Route
            path="compliance"
            element={
              <RequireRole roles={ADMIN_TIER}>
                <Wrap>
                  <CompliancePage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="iot"
            element={
              <Wrap>
                <IoTPage />
              </Wrap>
            }
          />
          <Route
            path="certificates"
            element={
              <Wrap>
                <CertificatesPage />
              </Wrap>
            }
          />
          {/* One certificate — the viewer families land on from the
              "Congratulations" notification (?reveal=1 plays the reveal).
              The API scopes the read, so it is open to every role here. */}
          <Route
            path="certificates/:id"
            element={
              <Wrap>
                <CertificateViewerPage />
              </Wrap>
            }
          />
          <Route
            path="recognition"
            element={
              <RequireRole roles={["CHAIRMAN", "ADMIN", "DIRECTOR", "PRINCIPAL", "TEACHER"]}>
                <Wrap>
                  <RecognitionPage />
                </Wrap>
              </RequireRole>
            }
          />
          {/* ID cards — one tree for the browser and the app: issuers get
              the desk, everyone else their own card (the API scopes it). */}
          <Route
            path="id-cards"
            element={
              <Wrap>
                <IdCardsPage />
              </Wrap>
            }
          />
          {/* The old ID-card studio pages redirect here, on the app too. */}
          <Route path="studio/ids" element={<Navigate to="/id-cards" replace />} />
          <Route path="studio/workspace" element={<Navigate to="/id-cards" replace />} />
          {/* /studio/certificates predates the Certificates section. It
              redirects on the app too (outside the native studio/* bounce
              below), so an old bookmark lands on the new section. */}
          <Route
            path="studio/certificates"
            element={<Navigate to="/certificates" replace />}
          />
          {/* Gate + visitor passes (2026-09-27) — rebuilt out of Studio as their
              own sections, phone-first for the security desk, so they work on
              web AND app (the native studio/* bounce below does not reach
              them). Each role sees its own view of the same component tree;
              the API scopes every read. The old /studio/* paths redirect. */}
          <Route
            path="gate-passes"
            element={
              <RequireRole roles={["CHAIRMAN", "ADMIN", "DIRECTOR", "PRINCIPAL", "STAFF", "TEACHER", "PARENT"]}>
                <Wrap>
                  <GatePassesPage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="visitor-passes"
            element={
              <RequireRole roles={["CHAIRMAN", "ADMIN", "DIRECTOR", "PRINCIPAL", "STAFF", "TEACHER", "PARENT"]}>
                <Wrap>
                  <VisitorPassesPage />
                </Wrap>
              </RequireRole>
            }
          />
          {/* Event Tickets (recognition-os): issuers get events, print sheets
              and the library; TEACHER / STAFF get the gate scanner only (the
              page decides, the API enforces). One tree for web and app. */}
          <Route
            path="event-tickets"
            element={
              <RequireRole roles={["CHAIRMAN", "ADMIN", "DIRECTOR", "PRINCIPAL", "TEACHER", "STAFF"]}>
                <Wrap>
                  <EventTicketsPage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="event-tickets/my"
            element={
              <RequireRole roles={["PARENT", "STUDENT"]}>
                <Wrap>
                  <MyTicketsPage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="event-tickets/:eventId"
            element={
              <RequireRole roles={["CHAIRMAN", "ADMIN", "DIRECTOR", "PRINCIPAL"]}>
                <Wrap>
                  <EventTicketDetailPage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="posters"
            element={
              <RequireRole roles={["CHAIRMAN", "ADMIN", "DIRECTOR", "PRINCIPAL"]}>
                <Wrap>
                  <PostersPage />
                </Wrap>
              </RequireRole>
            }
          />
          {/* School website (2026-10-01) — desktop tool, so the app goes home. */}
          <Route
            path="website"
            element={
              native ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <RequireRole roles={["CHAIRMAN", "ADMIN", "DIRECTOR", "PRINCIPAL"]}>
                  <Wrap>
                    <WebsitePage />
                  </Wrap>
                </RequireRole>
              )
            }
          />
          {/* The old Studio pages redirect on the app too (declared outside the
              native studio/* bounce), so an old bookmark lands on the section. */}
          <Route path="studio/event-tickets" element={<Navigate to="/event-tickets" replace />} />
          <Route path="studio/posters" element={<Navigate to="/posters" replace />} />
          <Route path="studio/social-posts" element={<Navigate to="/posters" replace />} />
          <Route path="studio/gate-passes" element={<Navigate to="/gate-passes" replace />} />
          <Route path="studio/visitor-passes" element={<Navigate to="/visitor-passes" replace />} />
          {/* Faircopy — handwritten/scanned paper → clean printable doc.
              Desktop-only creation tool (two-pane editor + print), so on
              native it bounces to the dashboard and is pruned from the app
              nav. Teachers + front-desk staff use it (question papers,
              receipts), alongside the admin tier. */}
          {native ? (
            <Route path="faircopy" element={<Navigate to="/dashboard" replace />} />
          ) : (
            <Route
              path="faircopy"
              element={
                <RequireRole roles={["CHAIRMAN", "ADMIN", "DIRECTOR", "PRINCIPAL", "TEACHER", "STAFF"]}>
                  <Wrap>
                    <FaircopyPage />
                  </Wrap>
                </RequireRole>
              }
            />
          )}

          {/* Studio home — the front door to the document sections
              (certificates, recognition, badges, ID cards, passes, tickets,
              posters) and the newsletter. Every section keeps its own route;
              this page only links to them. Same tree in the browser and the
              app (the app hides the two desktop-only editors). */}
          <Route
            path="studio"
            element={
              <RequireRole roles={["CHAIRMAN", "ADMIN", "DIRECTOR", "PRINCIPAL"]}>
                <Wrap>
                  <StudioHomePage />
                </Wrap>
              </RequireRole>
            }
          />
          {/* Native / PWA app: the newsletter editor is desktop-only, so
              every studio/* sub-route bounces to the dashboard there (nav
              hides it too, see sidebar NATIVE_HIDDEN_PATHS). */}
          {native ? (
            <Route path="studio/*" element={<Navigate to="/dashboard" replace />} />
          ) : (
          <>
          <Route
            path="studio/newsletter"
            element={
              <RequireRole roles={["CHAIRMAN", "ADMIN", "DIRECTOR", "PRINCIPAL"]}>
                <Wrap>
                  <StudioNewsletterPage />
                </Wrap>
              </RequireRole>
            }
          />
          </>
          )}
          {/* Hubba — AI chat assistant. Every role gets a different
              data scope: admin tiers see the school, TEACHER/STAFF/DRIVER
              see their own teaching/work scope, STUDENT/PARENT see only
              their own / their children's data (enforced by the API
              toolset, not the route). KIOSK and CONTROL_CENTER stay
              excluded — neither is a person having a conversation. */}
          <Route
            path="hubba"
            element={
              <RequireRole
                roles={[
                  "CHAIRMAN",
                  "DIRECTOR",
                  "ADMIN",
                  "PRINCIPAL",
                  "TEACHER",
                  "STAFF",
                  "DRIVER",
                  "STUDENT",
                  "PARENT",
                ]}
              >
                <Wrap>
                  <HubbaPage />
                </Wrap>
              </RequireRole>
            }
          />
          {/* Personal notifications inbox. Backed by GET /notifications,
              which is already user-scoped (userId = jwt.sub). Every
              role gets the same page — entries are different per
              user, not per role. */}
          <Route
            path="notifications"
            element={
              <Wrap>
                <NotificationsPage />
              </Wrap>
            }
          />
          <Route
            path="future"
            element={
              <Wrap>
                <FuturePage />
              </Wrap>
            }
          />
          <Route
            path="ai-tutor"
            element={
              <Wrap>
                <AiTutorPage />
              </Wrap>
            }
          />
          <Route
            path="automation"
            element={
              <RequireRole roles={ADMIN_TIER}>
                <Wrap>
                  <AutomationPage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="alumni"
            element={
              <Wrap>
                <AlumniPage />
              </Wrap>
            }
          />
          <Route
            path="science-lab"
            element={
              <Wrap>
                <ScienceLabPage />
              </Wrap>
            }
          />
          <Route
            path="neural"
            element={
              <RequirePreview path="/neural">
                <Wrap>
                  <NeuralPage />
                </Wrap>
              </RequirePreview>
            }
          />
          <Route
            path="metaverse"
            element={
              <Wrap>
                <MetaversePage />
              </Wrap>
            }
          />
          <Route
            path="scholarships"
            element={
              <RequireRole roles={ADMIN_TIER}>
                <Wrap>
                  <ScholarshipsPage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="accounting"
            element={
              <RequireRole roles={ADMIN_TIER}>
                <Wrap>
                  <FinancePage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="cpd"
            element={
              <Wrap>
                <CPDPage />
              </Wrap>
            }
          />
          <Route
            path="competency"
            element={
              <Wrap>
                <CompetencyPage />
              </Wrap>
            }
          />
          <Route
            path="hpcard"
            element={
              <Wrap>
                <HPCardPage />
              </Wrap>
            }
          />
          <Route
            path="forums"
            element={
              <Wrap>
                <ForumsPage />
              </Wrap>
            }
          />
          <Route
            path="ptm"
            element={
              <Wrap>
                <PTMPage />
              </Wrap>
            }
          />
          <Route
            path="surveys"
            element={
              <Wrap>
                <SurveysPage />
              </Wrap>
            }
          />
          <Route
            path="special-education"
            element={
              <Wrap>
                <SpecialEducationPage />
              </Wrap>
            }
          />
          <Route
            path="sel"
            element={
              <Wrap>
                <SELPage />
              </Wrap>
            }
          />
          <Route
            path="adaptive-learning"
            element={
              <Wrap>
                <AdaptiveLearningPage />
              </Wrap>
            }
          />
          <Route
            path="learning"
            element={
              <Wrap>
                <LearningPage />
              </Wrap>
            }
          />
          <Route
            path="scorm"
            element={
              <Wrap>
                <SCORMPage />
              </Wrap>
            }
          />
          <Route
            path="biometric"
            element={
              <RequireRole roles={ADMIN_TIER}>
                <Wrap>
                  <BiometricPage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="udise"
            element={
              <RequireRole roles={ADMIN_TIER}>
                <Wrap>
                  <UDISEPage />
                </Wrap>
              </RequireRole>
            }
          />
          {/* Mirrors the API: every /plagiarism/* route authorizes the admin tier
              + TEACHER + STAFF and nobody else, so a STUDENT/PARENT deep link
              was a page whose stats call 403s. lib/role-access.ts keeps
              STUDENT/PARENT off the prefix for the same reason; the TEACHER/STAFF
              allow-lists do not list it, so those roles are a guard-only admit. */}
          <Route
            path="plagiarism"
            element={
              <RequireRole roles={[...ADMIN_TIER_PLUS_STAFF, "TEACHER"]}>
                <Wrap>
                  <PlagiarismPage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="integrations"
            element={
              <RequireRole roles={ADMIN_TIER}>
                <Wrap>
                  <IntegrationsPage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="badges"
            element={
              <Wrap>
                <BadgesPage />
              </Wrap>
            }
          />
          {/* Donations & Fundraising (2026-09-30). Mirrors the API's VIEW_ROLES:
              admin tier + STAFF run the module, TEACHER raises campaigns,
              PARENT / STUDENT give. The shell's own guard narrows each screen
              (lib/donations/sections.ts) — a parent lands on /donations/give. */}
          <Route
            path="donations/*"
            element={
              <RequireRole roles={[...ADMIN_TIER, "STAFF", "TEACHER", "PARENT", "STUDENT"]}>
                <Wrap>
                  <DonationsModule />
                </Wrap>
              </RequireRole>
            }
          />
          {/* The Comms Hub: every tab calls an admin-tier-only endpoint
              (/whatsapp/*, /sms/*, /email/*, /push/logs, /communications/stats),
              so the admin tier is the whole audience. `/communication`
              (singular — messages, notices) is the page everyone else uses. */}
          <Route
            path="communications"
            element={
              <RequireRole roles={ADMIN_TIER}>
                <Wrap>
                  <CommunicationsPage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="communications/whatsapp"
            element={
              <RequireRole roles={ADMIN_TIER}>
                <Wrap>
                  <WhatsAppPage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="budget"
            element={
              <RequireRole roles={ADMIN_TIER}>
                <Wrap>
                  <BudgetPage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="dpdp"
            element={
              <Wrap>
                <DPDPPage />
              </Wrap>
            }
          />
          <Route
            path="audit"
            element={
              <RequireRole roles={ADMIN_TIER}>
                <Wrap>
                  <AuditLogPage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="schools"
            element={
              <RequireRole roles={["CHAIRMAN", "DIRECTOR"]}>
                <Wrap>
                  <SchoolsPage />
                </Wrap>
              </RequireRole>
            }
          />
          {/* The picked school's health rating. Overseer-only, mirroring the
              API: GET /dashboard/network/schools/:id/rating is an
              `overseerHubRoute` (403 for every other role, 404 for a school
              the caller holds no grant for). `lib/role-access.ts` denies the
              path to ADMIN / PRINCIPAL too, so the nav never offers a link
              that would bounce. */}
          <Route
            path="school-health"
            element={
              <RequireRole roles={["CHAIRMAN", "DIRECTOR"]}>
                <Wrap>
                  <SchoolHealthPage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="qa"
            element={
              <RequireRole roles={["CHAIRMAN"]}>
                <Wrap>
                  <QaPage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="settings/dashboard-builder"
            element={
              <Wrap>
                <DashboardBuilderPage />
              </Wrap>
            }
          />
          <Route
            path="settings/classes-setup"
            element={
              <Wrap>
                <DesktopOnly
                  native
                  title="Set up your school on desktop"
                  description="Academic setup — classes, subjects, periods and academic year — is a multi-step configuration best done on a larger screen. Open CampusHub on a desktop to continue."
                >
                  <ClassesSetupPage />
                </DesktopOnly>
              </Wrap>
            }
          />
          <Route
            path="concession-approvals"
            element={
              <RequireRole roles={ADMIN_TIER}>
                <Wrap>
                  <ConcessionApprovalsPage />
                </Wrap>
              </RequireRole>
            }
          />
          {/* Same gate as /concession-approvals and the dashboard tile that
              links here: the admin tier, the roles GET /student/actions/pending
              and the approve / reject endpoints authorize. */}
          <Route
            path="approvals"
            element={
              <RequireRole roles={ADMIN_TIER}>
                <Wrap>
                  <ApprovalsPage />
                </Wrap>
              </RequireRole>
            }
          />
          <Route
            path="*"
            element={
              <Wrap>
                <NotFoundPage />
              </Wrap>
            }
          />
        </Route>
      )}
      {/* Catch-all. Authenticated users land on /dashboard (or /onboarding
          if they haven't finished setup). Unauth users normally see the
          marketing home at /, but inside the native shell we skip marketing
          entirely and send them to /login — that's the only public surface
          the app is allowed to render. */}
      <Route
        path="*"
        element={
          <Navigate
            to={
              isAuthenticated
                ? (needsOnboarding ? "/onboarding" : "/dashboard")
                : (native ? "/login" : "/")
            }
            replace
          />
        }
      />
    </Routes>
  );
}
